import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Badge } from "@radix-ui/themes";
import { X, ChevronRight, Utensils, Star, Clock, MapPin, Phone, Mail, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { menuBookAPI } from '../services/api';

interface Restaurant {
  _id: string;
  slug: string;
  name: string;
  image: string;
  description: string;
  category: string;
  rating: { average: number; count: number };
  address: { street: string; city: string; state: string; coordinates?: { latitude: number; longitude: number } };
  contact: { phone?: string; email?: string };
  operatingHours: Record<string, { open: string; close: string; isOpen: boolean }>;
  deliveryInfo: { minimumOrder: number };
  isCurrentlyOpen: boolean | null;
  isFeatured: boolean;
}

interface Size {
  name: string;
  nameAr?: string;
  price: number;
  originalPrice?: number;
  isDefault: boolean;
  isAvailable: boolean;
}

interface FoodItem {
  _id: string;
  name: string;
  nameAr?: string;
  description: string;
  descriptionAr?: string;
  price: number;
  originalPrice?: number;
  sizes?: Size[];
  category: string;
  image: string;
  isAvailable: boolean;
  isFeatured: boolean;
  isPopular: boolean;
  rating?: { average: number; count: number };
  preparationTime?: string;
  ingredients?: string[];
  ingredientsAr?: string[];
  allergens?: string[];
  allergensAr?: string[];
  nutritionalInfo?: { calories?: number; protein?: number; carbs?: number; fat?: number };
}

// Category translation key mapping (same as TableOrderPage)
const categoryTranslationKeys: Record<string, string> = {
  'all': 'allItems',
  'fast-food': 'fastFood',
  'Fast food': 'fastFood',
  'fastfood': 'fastFood',
  'pizza': 'pizza',
  'Pizza': 'pizza',
  'asian': 'asian',
  'Asian': 'asian',
  'middle-eastern': 'middleEastern',
  'Middle-eastern': 'middleEastern',
  'Middle Eastern': 'middleEastern',
  'desserts': 'desserts',
  'Desserts': 'desserts',
  'dessert': 'desserts',
  'healthy': 'healthy',
  'Healthy': 'healthy',
  'coffee': 'coffee',
  'Coffee': 'coffee',
  'appetizer': 'appetizer',
  'Appetizer': 'appetizer',
  'main-dish': 'mainDish',
  'Main Dish': 'mainDish',
  'side-dish': 'sideDish',
  'Side Dish': 'sideDish',
  'sandwich': 'sandwich',
  'Sandwich': 'sandwich',
  'beverage': 'beverage',
  'Beverage': 'beverage',
  'drink': 'drink',
  'Drink': 'drink',
};

const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const MenuBookRestaurantPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, language, isRTL, setLanguage } = useLanguage();

  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [foodItems, setFoodItems] = useState<FoodItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showInfoPanel, setShowInfoPanel] = useState(false);

  // Item detail modal
  const [selectedItem, setSelectedItem] = useState<FoodItem | null>(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedSize, setSelectedSize] = useState<Size | null>(null);

  const viewTrackedRef = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!slug) return;
      setLoading(true);
      try {
        const { data } = await menuBookAPI.getRestaurantBySlug(slug);
        setRestaurant(data.restaurant);
        setFoodItems(data.foodItems || []);
        // Track restaurant page view (once per session)
        if (!viewTrackedRef.current) {
          viewTrackedRef.current = true;
          menuBookAPI.trackRestaurantView(slug);
        }
      } catch (e) {
        console.error('Error fetching restaurant:', e);
      }
      setLoading(false);
    };
    fetchData();
  }, [slug]);

  const getCategories = () => {
    const cats = Array.from(new Set(foodItems.map(item => item.category)));
    return ['all', ...cats];
  };

  const getCategoryLabel = (category: string) => {
    const key = categoryTranslationKeys[category];
    return key ? t(key) : category;
  };

  const getFilteredItems = () => {
    if (selectedCategory === 'all') return foodItems;
    return foodItems.filter(item => item.category === selectedCategory);
  };

  const openItemDetails = (item: FoodItem) => {
    setSelectedItem(item);
    setShowItemModal(true);
    // Track item detail view
    menuBookAPI.trackItemView(item._id);
    if (item.sizes && item.sizes.length > 0) {
      const defaultSize = item.sizes.find(s => s.isDefault && s.isAvailable) ||
        item.sizes.find(s => s.isAvailable) ||
        item.sizes[0];
      setSelectedSize(defaultSize);
    } else {
      setSelectedSize(null);
    }
  };

  const closeItemModal = () => {
    setShowItemModal(false);
    setSelectedItem(null);
    setSelectedSize(null);
  };

  const todayIndex = new Date().getDay();

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Helmet>
          <title>{t("loading")}... | Turbo Menu Book</title>
        </Helmet>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">{t("loading")}</p>
        </div>
      </div>
    );
  }

  // Not found
  if (!restaurant) {
    return (
      <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <Helmet>
          <title>{t("noRestaurantsFound")} | Turbo Menu Book</title>
        </Helmet>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t("noRestaurantsFound")}</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{t("tryDifferentSearch")}</p>
          <button
            onClick={() => navigate('/menu')}
            className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl py-3 font-medium transition-colors"
          >
            {t("back")}
          </button>
        </div>
      </div>
    );
  }

  // Generate Schema.org JSON-LD
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    "name": restaurant.name,
    "image": restaurant.image,
    "description": restaurant.description,
    "servesCuisine": getCategoryLabel(restaurant.category),
    "telephone": restaurant.contact?.phone || "",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": restaurant.address?.street || "",
      "addressLocality": restaurant.address?.city || "",
      "addressRegion": restaurant.address?.state || "",
      "addressCountry": "SD"
    },
    ...(restaurant.rating?.count > 0 && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": restaurant.rating.average,
        "reviewCount": restaurant.rating.count
      }
    }),
    "hasMenu": window.location.href,
  };

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Helmet>
        <title>{restaurant.name} Menu | Turbo</title>
        <meta name="description" content={`View the full menu for ${restaurant.name}. ${restaurant.description || getCategoryLabel(restaurant.category)}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="restaurant.menu" />
        <meta property="og:title" content={`${restaurant.name} Menu | Turbo`} />
        <meta property="og:description" content={`View the full menu for ${restaurant.name}. ${restaurant.description || getCategoryLabel(restaurant.category)}`} />
        {restaurant.image && <meta property="og:image" content={restaurant.image} />}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${restaurant.name} Menu | Turbo`} />
        <meta name="twitter:description" content={`View the full menu for ${restaurant.name}. ${restaurant.description || getCategoryLabel(restaurant.category)}`} />
        {restaurant.image && <meta name="twitter:image" content={restaurant.image} />}
        
        {/* Schema.org Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-30 bg-white dark:bg-gray-800 shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/menu')}
              className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <ChevronRight className={`w-5 h-5 text-gray-700 dark:text-gray-300 ${isRTL ? '' : 'rotate-180'}`} />
            </button>
            {restaurant.image ? (
              <img
                src={restaurant.image}
                alt={restaurant.name}
                className="w-10 h-10 rounded-xl object-cover border border-gray-200 dark:border-gray-600"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Utensils className="w-5 h-5 text-red-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                {restaurant.name}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {getCategoryLabel(restaurant.category)}
              </p>
            </div>
            {restaurant.isCurrentlyOpen !== null && (
              <Badge color={restaurant.isCurrentlyOpen ? 'green' : 'red'} size="2">
                {t(restaurant.isCurrentlyOpen ? 'currentlyOpen' : 'currentlyClosed')}
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* Category Tabs - Fixed below header */}
      <div className="fixed top-[60px] left-0 right-0 z-20 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-3 px-4 py-4">
            {getCategories().map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${selectedCategory === category
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
              >
                {getCategoryLabel(category)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="pt-[120px] pb-24 px-4">
        {/* Restaurant Info Panel (Collapsible) */}
        <div className="mb-4">
          <button
            onClick={() => setShowInfoPanel(!showInfoPanel)}
            className="w-full bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Info className="w-5 h-5 text-blue-600" />
              </div>
              <span className="font-medium text-gray-900 dark:text-white">{t("restaurantInfo")}</span>
            </div>
            {showInfoPanel ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {showInfoPanel && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 mt-2 p-6 space-y-4 animate-slide-down">
              {/* Description */}
              {restaurant.description && (
                <p className="text-gray-600 dark:text-gray-400">{restaurant.description}</p>
              )}

              {/* Address */}
              {restaurant.address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    {[restaurant.address.street, restaurant.address.city, restaurant.address.state].filter(Boolean).join(', ')}
                  </p>
                </div>
              )}

              {/* Contact */}
              {restaurant.contact?.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <a href={`tel:${restaurant.contact.phone}`} className="text-red-600 text-sm font-medium">{restaurant.contact.phone}</a>
                </div>
              )}
              {restaurant.contact?.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <a href={`mailto:${restaurant.contact.email}`} className="text-red-600 text-sm font-medium">{restaurant.contact.email}</a>
                </div>
              )}

              {/* Delivery Minimum */}
              {restaurant.deliveryInfo?.minimumOrder > 0 && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("deliveryMinimum")}: <span className="text-red-600 font-bold">SDG {restaurant.deliveryInfo.minimumOrder.toFixed(2)}</span>
                  </span>
                </div>
              )}

              {/* Operating Hours */}
              {restaurant.operatingHours && Object.keys(restaurant.operatingHours).length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {t("operatingHoursTitle")}
                  </h4>
                  <div className="space-y-1">
                    {dayKeys.map((day, idx) => {
                      const hours = restaurant.operatingHours[day];
                      const isToday = idx === todayIndex;
                      return (
                        <div
                          key={day}
                          className={`flex items-center justify-between text-sm py-2 px-3 rounded-lg ${
                            isToday ? 'bg-red-50 dark:bg-red-900/20 font-medium' : ''
                          }`}
                        >
                          <span className="text-gray-700 dark:text-gray-300">
                            {t(day)} {isToday && <span className="text-red-600 text-xs">({t("today")})</span>}
                          </span>
                          <span className={`${isToday ? 'text-red-600' : 'text-gray-500 dark:text-gray-400'}`}>
                            {hours ? `${hours.open} - ${hours.close}` : t("closed")}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Menu Items Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {getFilteredItems().map(item => {
            const itemName = language === 'ar' && item.nameAr ? item.nameAr : item.name;
            const itemDesc = language === 'ar' && item.descriptionAr ? item.descriptionAr : item.description;

            // Calculate price display
            const hasSizes = item.sizes && item.sizes.length > 0;
            const availableSizes = hasSizes ? item.sizes!.filter(s => s.isAvailable) : [];
            let priceDisplay = `SDG ${item.price.toFixed(2)}`;
            if (availableSizes.length > 0) {
              const prices = availableSizes.map(s => s.price).sort((a, b) => a - b);
              const minPrice = prices[0];
              const maxPrice = prices[prices.length - 1];
              priceDisplay = minPrice === maxPrice
                ? `SDG ${minPrice.toFixed(2)}`
                : `SDG ${minPrice.toFixed(2)} - ${maxPrice.toFixed(2)}`;
            }

            return (
              <div
                key={item._id}
                className={`bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden border border-gray-100 dark:border-gray-700 ${
                  !item.isAvailable ? 'opacity-60' : ''
                }`}
              >
                {/* Item Image */}
                <div
                  className="relative h-40 bg-gray-100 dark:bg-gray-700 cursor-pointer"
                  onClick={() => openItemDetails(item)}
                >
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={itemName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Utensils className="w-12 h-12 text-gray-300 dark:text-gray-600" />
                    </div>
                  )}
                  {/* Category badge */}
                  <div className={`absolute top-2 ${isRTL ? 'right-2' : 'left-2'}`}>
                    <span className="px-2 py-1 text-xs font-medium bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-300 rounded-full">
                      {getCategoryLabel(item.category)}
                    </span>
                  </div>
                  {/* Rating badge */}
                  {item.rating && item.rating.count > 0 && (
                    <div className={`absolute top-2 ${isRTL ? 'left-2' : 'right-2'}`}>
                      <span className="px-2 py-1 text-xs font-medium bg-yellow-400 text-yellow-900 rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3 fill-current" />
                        {item.rating.average.toFixed(1)}
                      </span>
                    </div>
                  )}
                  {/* Unavailable overlay */}
                  {!item.isAvailable && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <span className="px-4 py-2 bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-300 rounded-full font-medium text-sm">
                        {t("itemUnavailable")}
                      </span>
                    </div>
                  )}
                  {/* View details indicator */}
                  {item.isAvailable && (
                    <div className="absolute bottom-2 right-2 left-2 flex justify-center">
                      <span className="px-3 py-1 text-xs font-medium bg-black/50 text-white rounded-full flex items-center gap-1">
                        {hasSizes ? t("viewRestaurantMenu") : t("viewRestaurantMenu")}
                        <ChevronRight className="w-3 h-3" />
                      </span>
                    </div>
                  )}
                </div>

                {/* Item Info */}
                <div className="p-4">
                  <div className="flex justify-between items-start gap-2 mb-1">
                    <h3
                      className="font-semibold text-gray-900 dark:text-white line-clamp-1 cursor-pointer hover:text-red-600"
                      onClick={() => openItemDetails(item)}
                    >
                      {itemName}
                    </h3>
                    <span className="font-bold text-red-600 whitespace-nowrap text-sm">{priceDisplay}</span>
                  </div>
                  {/* Rating and reviews count */}
                  {item.rating && item.rating.count > 0 && (
                    <button
                      onClick={() => openItemDetails(item)}
                      className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 mb-2 hover:text-yellow-500"
                    >
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span>{item.rating.average.toFixed(1)}</span>
                      <span>({item.rating.count})</span>
                    </button>
                  )}
                  {itemDesc && (
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-4 line-clamp-2">{itemDesc}</p>
                  )}
                  <button
                    onClick={() => openItemDetails(item)}
                    className={`w-full bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 font-medium transition-colors ${
                      !item.isAvailable ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    disabled={!item.isAvailable}
                  >
                    {t("viewMenu")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {getFilteredItems().length === 0 && (
          <div className="text-center py-12">
            <Utensils className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">{t("noItemsInCategory")}</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <div className="text-center py-6 text-xs text-gray-400 dark:text-gray-500">
        {t("poweredByTurbo")}
      </div>

      {/* Custom Styles */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-down {
          animation: slide-down 0.2s ease-out;
        }
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>

      {/* Food Item Detail Modal */}
      {showItemModal && selectedItem && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={closeItemModal}
          />

          {/* Modal */}
          <div className="fixed inset-x-4 top-[10%] bottom-[10%] z-50 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden max-w-lg mx-auto">
            {/* Modal Header with Image */}
            <div className="relative h-48 bg-gray-100 dark:bg-gray-700 flex-shrink-0">
              {selectedItem.image && (
                <img
                  src={selectedItem.image}
                  alt={language === 'ar' && selectedItem.nameAr ? selectedItem.nameAr : selectedItem.name}
                  className="w-full h-full object-cover"
                />
              )}
              <button
                onClick={closeItemModal}
                className="absolute top-4 right-4 w-10 h-10 bg-white/90 dark:bg-gray-800/90 rounded-full flex items-center justify-center shadow-lg"
              >
                <X className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              </button>
              {/* Rating badge */}
              {selectedItem.rating && selectedItem.rating.count > 0 && (
                <div className="absolute bottom-4 left-4">
                  <span className="px-3 py-1.5 text-sm font-medium bg-yellow-400 text-yellow-900 rounded-full flex items-center gap-1.5 shadow-lg">
                    <Star className="w-4 h-4 fill-current" />
                    {selectedItem.rating.average.toFixed(1)}
                    <span className="text-yellow-700">({selectedItem.rating.count})</span>
                  </span>
                </div>
              )}
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Item Info */}
              <div className="mb-6">
                <div className="flex justify-between items-start gap-2 mb-2">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {language === 'ar' && selectedItem.nameAr ? selectedItem.nameAr : selectedItem.name}
                  </h2>
                  <span className="text-xl font-bold text-red-600">
                    SDG {(selectedSize ? selectedSize.price : selectedItem.price).toFixed(2)}
                  </span>
                </div>
                <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full mb-3">
                  {getCategoryLabel(selectedItem.category)}
                </span>
                {/* Star Rating Display */}
                {selectedItem.rating && selectedItem.rating.count > 0 && (
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-5 h-5 ${star <= Math.round(selectedItem.rating!.average) ? 'text-yellow-400 fill-current' : 'text-gray-300 dark:text-gray-600'}`}
                        />
                      ))}
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{selectedItem.rating.average.toFixed(1)}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">({selectedItem.rating.count})</span>
                  </div>
                )}
                <p className="text-gray-600 dark:text-gray-400">
                  {language === 'ar' && selectedItem.descriptionAr ? selectedItem.descriptionAr : selectedItem.description}
                </p>
              </div>

              {/* Size Selection */}
              {selectedItem.sizes && selectedItem.sizes.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3">{t("size")}</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedItem.sizes.filter(s => s.isAvailable).map((size) => {
                      const sizeName = language === 'ar' && size.nameAr ? size.nameAr : size.name;
                      const isSelected = selectedSize?.name === size.name;
                      return (
                        <button
                          key={size.name}
                          onClick={() => setSelectedSize(size)}
                          className={`p-3 rounded-xl border-2 transition-all ${isSelected
                            ? 'border-red-600 bg-red-50 dark:bg-red-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-red-300'
                            }`}
                        >
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{sizeName}</div>
                          <div className={`text-sm ${isSelected ? 'text-red-600' : 'text-gray-500 dark:text-gray-400'}`}>
                            SDG {size.price.toFixed(2)}
                            {size.originalPrice && size.originalPrice > size.price && (
                              <span className="ml-1 line-through text-gray-400 text-xs">SDG {size.originalPrice.toFixed(2)}</span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Prep Time */}
              {selectedItem.preparationTime && (
                <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t("prepTime")}</p>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">{selectedItem.preparationTime}</p>
                  </div>
                </div>
              )}

              {/* Ingredients */}
              {selectedItem.ingredients && selectedItem.ingredients.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">{t("ingredients")}</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {(language === 'ar' && selectedItem.ingredientsAr?.length ? selectedItem.ingredientsAr : selectedItem.ingredients).map((ing, i) => (
                      <span key={i} className="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs">
                        {ing}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Allergens */}
              {selectedItem.allergens && selectedItem.allergens.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">{t("allergens")}</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {(language === 'ar' && selectedItem.allergensAr?.length ? selectedItem.allergensAr : selectedItem.allergens).map((a, i) => (
                      <span key={i} className="px-2.5 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full text-xs font-medium">
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Nutritional Info */}
              {selectedItem.nutritionalInfo && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">{t("nutritionalInfo")}</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedItem.nutritionalInfo.calories != null && (
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-center">
                        <p className="text-lg font-bold text-red-600">{selectedItem.nutritionalInfo.calories}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("calories")}</p>
                      </div>
                    )}
                    {selectedItem.nutritionalInfo.protein != null && (
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-center">
                        <p className="text-lg font-bold text-blue-600">{selectedItem.nutritionalInfo.protein}g</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("protein")}</p>
                      </div>
                    )}
                    {selectedItem.nutritionalInfo.carbs != null && (
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-center">
                        <p className="text-lg font-bold text-yellow-600">{selectedItem.nutritionalInfo.carbs}g</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("carbs")}</p>
                      </div>
                    )}
                    {selectedItem.nutritionalInfo.fat != null && (
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-center">
                        <p className="text-lg font-bold text-purple-600">{selectedItem.nutritionalInfo.fat}g</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("fat")}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800 flex-shrink-0">
              <button
                onClick={closeItemModal}
                className="w-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl h-14 text-lg font-semibold transition-colors"
              >
                {t("close")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MenuBookRestaurantPage;
